import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

const CursorContainer = styled.div`
  pointer-events: none;
  z-index: 9999999; /* Maximize z-index to stay above chatbot and modals */
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;

  @media (max-width: 768px) {
    display: none;
  }
`;

const CursorDot = styled.div`
  width: 10px;
  height: 10px;
  background-color: #00e6ff;
  border-radius: 50%;
  position: absolute;
  pointer-events: none;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 10px #00e6ff, 0 0 20px #00e6ff;
  transition: width 0.2s, height 0.2s;
`;

const CursorOutline = styled.div`
  width: 40px;
  height: 40px;
  border: 2px dashed rgba(0, 230, 255, 0.6);
  border-radius: 50%;
  position: absolute;
  pointer-events: none;
  transform: translate(-50%, -50%);
  transition: width 0.2s, height 0.2s, background-color 0.2s, border-color 0.2s;
  
  /* Rotating animation for the dashed border */
  animation: rotateDashes 4s linear infinite;

  @keyframes rotateDashes {
    0% { transform: translate(-50%, -50%) rotate(0deg); }
    100% { transform: translate(-50%, -50%) rotate(360deg); }
  }
`;

const CustomCursor = () => {
    const dotRef = useRef(null);
    const outlineRef = useRef(null);

    const posParams = useRef({
        mouseX: 0,
        mouseY: 0,
        outlineX: 0,
        outlineY: 0
    });

    useEffect(() => {
        let animationFrameId;
        const params = posParams.current;

        const onMouseMove = (e) => {
            params.mouseX = e.clientX;
            params.mouseY = e.clientY;

            if (dotRef.current) {
                dotRef.current.style.left = `${params.mouseX}px`;
                dotRef.current.style.top = `${params.mouseY}px`;
            }
        };

        const render = () => {
            params.outlineX += (params.mouseX - params.outlineX) * 0.15;
            params.outlineY += (params.mouseY - params.outlineY) * 0.15;

            if (outlineRef.current) {
                outlineRef.current.style.left = `${params.outlineX}px`;
                outlineRef.current.style.top = `${params.outlineY}px`;
            }

            animationFrameId = requestAnimationFrame(render);
        };

        const onMouseDown = () => {
            if (outlineRef.current) {
                outlineRef.current.style.transform = "translate(-50%, -50%) scale(0.8)";
                outlineRef.current.style.backgroundColor = "rgba(0, 230, 255, 0.2)";
            }
            if (dotRef.current) {
                dotRef.current.style.transform = "translate(-50%, -50%) scale(0.5)";
            }
        };

        const onMouseUp = () => {
            if (outlineRef.current) {
                outlineRef.current.style.transform = "translate(-50%, -50%) scale(1)";
                outlineRef.current.style.backgroundColor = "transparent";
            }
            if (dotRef.current) {
                dotRef.current.style.transform = "translate(-50%, -50%) scale(1)";
            }
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mousedown", onMouseDown);
        document.addEventListener("mouseup", onMouseUp);

        // Initial render call
        render();

        const interactiveSelectors = 'a, button, input, textarea, select, [role="button"], .interactive';

        const applyHoverEffects = () => {
            const elements = document.querySelectorAll(interactiveSelectors);
            elements.forEach((el) => {
                if (!el.dataset.hasCursorHover) {
                    el.dataset.hasCursorHover = "true";
                    el.addEventListener("mouseenter", () => {
                        if (outlineRef.current) {
                            outlineRef.current.style.width = "60px";
                            outlineRef.current.style.height = "60px";
                            outlineRef.current.style.backgroundColor = "rgba(0, 230, 255, 0.1)";
                        }
                    });
                    el.addEventListener("mouseleave", () => {
                        if (outlineRef.current) {
                            outlineRef.current.style.width = "40px";
                            outlineRef.current.style.height = "40px";
                            outlineRef.current.style.backgroundColor = "transparent";
                        }
                    });
                }
            });
        };

        // Apply on mount
        applyHoverEffects();

        // Observer for dynamic changes
        const observer = new MutationObserver(applyHoverEffects);
        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mousedown", onMouseDown);
            document.removeEventListener("mouseup", onMouseUp);
            cancelAnimationFrame(animationFrameId);
            observer.disconnect();
        };
    }, []);

    return (
        <CursorContainer>
            <CursorOutline className="cursor-outline" ref={outlineRef} />
            <CursorDot className="cursor-dot" ref={dotRef} />
        </CursorContainer>
    );
};

export default CustomCursor;
